import React from 'react';
import { useTranslation } from 'react-i18next';
import { agenteInfo } from '../mock/properties';
import {
  Phone, Mail, MapPin, Clock, Award, Briefcase, Target, Users,
  TrendingUp, Shield, MessageCircle, Building2
} from 'lucide-react';

const About = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading">
              {t('about.title')}
            </h1>
            <p className="text-xl text-blue-100 font-body">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Perfil del agente */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="md:flex">
              <div className="md:w-1/3">
                <img
                  src={agenteInfo.foto}
                  alt={t('about.agent.name')}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="md:w-2/3 p-8">
                <h2 className="text-3xl font-bold text-blue-900 mb-2 font-heading">{t('about.agent.name')}</h2>
                <p className="text-xl text-amber-600 font-semibold mb-1 font-heading">{t('about.agent.company')}</p>
                <p className="text-lg text-gray-600 font-medium mb-4 font-body">{t('about.agent.title')}</p>
                <p className="text-gray-700 leading-relaxed mb-6 font-body">
                  {t('about.agent.description')}
                </p>

                {/* Contacto rápido */}
                <div className="flex flex-wrap gap-4">
                  <a
                    href={`https://wa.me/${agenteInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md font-body"
                  >
                    <MessageCircle size={20} />
                    {t('about.contact.whatsappBtn')}
                  </a>
                  <a
                    href={`tel:${agenteInfo.telefono}`}
                    className="flex items-center gap-2 bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors font-medium shadow-md font-body"
                  >
                    <Phone size={20} />
                    {t('about.contact.callBtn')}
                  </a>
                  <a
                    href={`mailto:${agenteInfo.email}`}
                    className="flex items-center gap-2 bg-amber-500 text-white px-6 py-3 rounded-lg hover:bg-amber-600 transition-colors font-medium shadow-md font-body"
                  >
                    <Mail size={20} />
                    {t('about.contact.emailBtn')}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Valores y ventajas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-blue-900" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 font-heading">{t('about.values.trust')}</h3>
              <p className="text-gray-600 font-body">
                {t('about.values.trustDesc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="text-amber-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 font-heading">{t('about.values.precision')}</h3>
              <p className="text-gray-600 font-body">
                {t('about.values.precisionDesc')}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 font-heading">{t('about.values.results')}</h3>
              <p className="text-gray-600 font-body">
                {t('about.values.resultsDesc')}
              </p>
            </div>
          </div>

          {/* Experiencia destacada */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-xl shadow-lg p-8 mb-8 text-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <Users className="mx-auto mb-3 text-amber-400" size={48} />
                <p className="text-4xl font-bold mb-2 font-heading">25+</p>
                <p className="text-blue-200 font-body">{t('about.experience.customerService')}</p>
              </div>
              <div>
                <Building2 className="mx-auto mb-3 text-amber-400" size={48} />
                <p className="text-4xl font-bold mb-2 font-heading">5</p>
                <p className="text-blue-200 font-body">{t('about.experience.realEstate')}</p>
              </div>
              <div>
                <Users className="mx-auto mb-3 text-amber-400" size={48} />
                <p className="text-4xl font-bold mb-2 font-heading">800+</p>
                <p className="text-blue-200 font-body">{t('about.experience.network')}</p>
              </div>
            </div>
          </div>

          {/* Certificaciones */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="text-amber-600" size={32} />
              <h2 className="text-2xl font-bold text-gray-800 font-heading">{t('about.certifications')}</h2>
            </div>
            
            {/* Certificado CONOCER/AMECAP */}
            <div className="mb-8 bg-gradient-to-br from-blue-50 to-amber-50 rounded-xl p-6 border-2 border-amber-200">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/2">
                  <img
                    src="https://customer-assets.emergentagent.com/job_property-media/artifacts/6g5powug_38601725-FRANCO%20DIMURO%20-AMECAP.pdf"
                    alt="Certificado CONOCER - Franco Dimuro"
                    className="w-full rounded-lg shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                    onClick={() => window.open('https://customer-assets.emergentagent.com/job_property-media/artifacts/6g5powug_38601725-FRANCO%20DIMURO%20-AMECAP.pdf', '_blank')}
                  />
                </div>
                <div className="md:w-1/2 flex flex-col justify-center">
                  <h3 className="text-xl font-bold text-blue-900 mb-3 font-heading">
                    {t('about.certificate.title')}
                  </h3>
                  <div className="space-y-2 text-gray-700 font-body">
                    <p className="flex items-start gap-2">
                      <Award className="text-amber-600 flex-shrink-0 mt-1" size={18} />
                      <span><strong>{t('about.certificate.standard')}</strong> {t('about.certificate.standardDesc')}</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Award className="text-amber-600 flex-shrink-0 mt-1" size={18} />
                      <span><strong>{t('about.certificate.organization')}</strong> {t('about.certificate.organizationDesc')}</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Award className="text-amber-600 flex-shrink-0 mt-1" size={18} />
                      <span><strong>{t('about.certificate.certifiedBy')}</strong> AMECAP / AMPI</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <Award className="text-amber-600 flex-shrink-0 mt-1" size={18} />
                      <span><strong>{t('about.certificate.folio')}</strong> D-0038601725</span>
                    </p>
                  </div>
                  <button
                    onClick={() => window.open('https://customer-assets.emergentagent.com/job_property-media/artifacts/6g5powug_38601725-FRANCO%20DIMURO%20-AMECAP.pdf', '_blank')}
                    className="mt-4 bg-gradient-to-r from-blue-900 to-blue-800 text-white px-6 py-3 rounded-lg hover:from-blue-800 hover:to-blue-700 transition-all font-medium shadow-md hover:shadow-lg font-body"
                  >
                    {t('about.certificate.viewFull')}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Otras certificaciones */}
            <div className="space-y-3">
              {t('about.agent.certificationsList', { returnObjects: true }).map((cert, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                  <Award className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700 font-body">{cert}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Servicios */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Briefcase className="text-amber-600" size={32} />
              <h2 className="text-2xl font-bold text-gray-800 font-heading">{t('about.services')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {t('about.agent.servicesList', { returnObjects: true }).map((servicio, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
                  <div className="bg-amber-500 p-1 rounded-full flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                  <span className="text-gray-700 font-medium font-body">{servicio}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Información de contacto */}
          <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl shadow-lg p-8 text-white">
            <h2 className="text-2xl font-bold mb-6 font-heading">{t('about.contact.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <Phone className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="text-blue-200 text-sm mb-1 font-body">{t('about.contact.phone')}</p>
                  <a href={`tel:${agenteInfo.telefono}`} className="text-lg font-semibold hover:text-amber-400 transition-colors font-body">
                    {agenteInfo.telefono}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="text-blue-200 text-sm mb-1 font-body">{t('about.contact.email')}</p>
                  <a href={`mailto:${agenteInfo.email}`} className="text-lg font-semibold hover:text-amber-400 transition-colors break-all font-body">
                    {agenteInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="text-blue-200 text-sm mb-1 font-body">{t('about.contact.office')}</p>
                  <p className="text-lg font-semibold font-body">{t('about.agent.officeLocation')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="text-amber-400 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="text-blue-200 text-sm mb-1 font-body">{t('about.contact.schedule')}</p>
                  <p className="text-lg font-semibold font-body">{t('about.agent.schedule')}</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 pt-8 border-t border-blue-700">
              <p className="text-center text-xl mb-4 font-body">{t('about.contact.ready')}</p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href={`https://wa.me/${agenteInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 transition-colors shadow-lg font-body"
                >
                  {t('about.contact.whatsapp')}
                </a>
                <a
                  href={`tel:${agenteInfo.telefono}`}
                  className="bg-amber-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-amber-600 transition-colors shadow-lg font-body"
                >
                  {t('about.contact.callNow')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
